import React, { useState } from 'react';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { FileText, Link as LinkIcon, Upload, X, File } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TechnicalDocumentsManager = ({ documents, onChange }) => {
  const [uploadingFiles, setUploadingFiles] = useState({});

  const handleDocumentChange = (category, index, field, value) => {
    const updatedDocuments = { ...documents };
    updatedDocuments[category][index][field] = value;
    onChange(updatedDocuments);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const handleFileUpload = async (category, index, file) => {
    // Validação de extensões e tamanho
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.dwg', '.zip'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedExtensions.includes(fileExtension)) {
      alert(`Formato não permitido. Use: ${allowedExtensions.join(', ')}`);
      return;
    }

    // Verificar tamanho (20MB)
    if (file.size > 20 * 1024 * 1024) {
      alert('Arquivo muito grande. Tamanho máximo: 20MB');
      return;
    }

    const uploadKey = `${category}-${index}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/upload-document`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Update document with uploaded file URL
      const updatedDocuments = { ...documents };
      // Se a URL não começa com http, usar /api/uploads/
      const fileUrl = response.data.url.startsWith('http') 
        ? response.data.url 
        : `/api/uploads/${response.data.url}`;
      updatedDocuments[category][index].url = fileUrl;
      if (!updatedDocuments[category][index].name) {
        updatedDocuments[category][index].name = file.name;
      }
      onChange(updatedDocuments);

      alert('Arquivo enviado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao fazer upload';
      alert(errorMsg);
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const clearDocument = (category, index) => {
    const updatedDocuments = { ...documents };
    updatedDocuments[category][index] = { url: '', name: '' };
    onChange(updatedDocuments);
  };

  const renderDocumentSection = (title, category) => (
    <div className="mb-8">
      <h4 className="text-base font-semibold text-gray-800 mb-4 flex items-center">
        <FileText size={20} className="mr-2 text-[#00A86B]" />
        {title}
      </h4>
      <div className="space-y-3">
        {documents[category].map((doc, index) => {
          const uploadKey = `${category}-${index}`;
          const isUploading = uploadingFiles[uploadKey];

          return (
            <Card key={index} className="p-4 bg-gray-50">
              <div className="flex items-start gap-3">
                <span className="text-sm font-medium text-gray-600 w-8 mt-2">#{index + 1}</span>
                <div className="flex-1 space-y-3">
                  {/* Nome/Descrição */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Nome/Descrição</label>
                    <Input
                      type="text"
                      placeholder="Ex: Planta Baixa"
                      value={doc.name}
                      onChange={(e) => handleDocumentChange(category, index, 'name', e.target.value)}
                      className="text-sm"
                    />
                  </div>

                  {/* Upload ou Link */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Documento</label>
                    <div className="flex gap-2">
                      {/* Upload Button */}
                      <div className="relative flex-1">
                        <input
                          type="file"
                          id={`file-${category}-${index}`}
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleFileUpload(category, index, file);
                              e.target.value = ''; // Limpa input
                            }
                          }}
                          accept=".pdf,.doc,.docx,.dwg,.zip"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById(`file-${category}-${index}`).click()}
                          disabled={isUploading}
                          className="w-full"
                        >
                          <Upload size={16} className="mr-2" />
                          {isUploading ? 'Enviando...' : 'Upload Arquivo'}
                        </Button>
                      </div>

                      {/* URL Input */}
                      <div className="flex-1">
                        <div className="flex gap-1">
                          <Input
                            type="text"
                            placeholder="ou cole o link aqui"
                            value={doc.url}
                            onChange={(e) => handleDocumentChange(category, index, 'url', e.target.value)}
                            className="text-sm"
                          />
                          {doc.url && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => clearDocument(category, index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X size={16} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Display current file */}
                    {doc.url && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-gray-600 bg-white p-2 rounded border">
                        <File size={14} className="text-[#00A86B]" />
                        <span className="flex-1 truncate">{doc.url}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="border-t pt-6">
        <h3 className="text-xl font-bold text-gray-800 mb-6">Documentos Técnicos</h3>
        <p className="text-sm text-gray-600 mb-2">
          Faça upload de arquivos ou adicione links para documentos técnicos. 
          Os arquivos enviados poderão ser anexados em emails quando o cliente comprar o projeto.
        </p>
        <p className="text-xs text-gray-500 mb-6">
          <strong>Formatos aceitos:</strong> PDF, DOC, DOCX, DWG, ZIP (máx. 20MB por arquivo)
        </p>
        
        {renderDocumentSection('Projeto Arquitetônico', 'arquitetonico')}
        {renderDocumentSection('Projeto de Interiores', 'interiores')}
      </div>
    </div>
  );
};

export default TechnicalDocumentsManager;
